import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, SpecraButton } from '@mono/ui-components';
import { SpecraOverlayDialog } from '@mono/ui-components/captains';
import { SpecraTextarea } from '@mono/ui-components/ensigns';
import { SpecraDateField, SpecraFormField, SpecraOverlayFooter } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ConfirmDeleteDialog } from '../../../Delivery/_shared/components/ConfirmDeleteDialog';
import { DocumentPickerField } from '../../documents/DocumentPickerField';
import { openDesignation } from './companyIdentity';
import { timestampToDay } from './corporateEvents';
import { useMarkPartner, useRemovePartner, useUpdatePartner } from './hooks';
const NOTES_MAX = 2000;
const today = ()=>new Date().toISOString().slice(0, 10);
/** Mark as partner / edit / end partnership (SAD-1333). */ export function PartnerDialog({ open, onOpenChange, companyId, companyName, mode, designation }) {
    const mark = useMarkPartner();
    const update = useUpdatePartner();
    const [since, setSince] = useState('');
    const [ended, setEnded] = useState('');
    const [notes, setNotes] = useState('');
    const [doc, setDoc] = useState(undefined);
    useEffect(()=>{
        if (!open) return;
        setSince(timestampToDay(designation?.effectiveStart));
        setEnded(mode === 'end' ? today() : timestampToDay(designation?.effectiveEnd));
        setNotes(designation?.notes ?? '');
        setDoc(undefined);
    }, [
        open,
        mode,
        designation
    ]);
    const datesInvalid = !!since && !!ended && ended < since;
    const notesTooLong = notes.length > NOTES_MAX;
    const pending = mark.isPending || update.isPending;
    const canSave = !datesInvalid && !notesTooLong && (mode !== 'end' || !!ended);
    const save = ()=>{
        const fields = {
            since,
            ended: mode === 'add' ? '' : ended,
            notes,
            sourceArtifactId: doc?.artifactId ?? designation?.sourceArtifactId ?? ''
        };
        const done = {
            onSuccess: ()=>onOpenChange(false)
        };
        if (mode === 'add') mark.mutate({
            companyId,
            fields
        }, done);
        else if (designation) update.mutate({
            id: designation.id,
            fields
        }, done);
    };
    const title = mode === 'add' ? `Mark ${companyName} as a partner` : mode === 'end' ? 'End partnership' : 'Edit partnership';
    return /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
        open: open,
        onOpenChange: onOpenChange,
        title: title,
        description: mode === 'end' ? 'The partnership stays in the history as a former partner.' : 'A partner of ours at the company level, whatever pursuits and programs say.',
        size: "md",
        testId: Testids.registryCompany.partnerDialog,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4",
                children: [
                    mode !== 'end' && /*#__PURE__*/ _jsx(SpecraDateField, {
                        label: "Partner since",
                        hint: "Leave empty if you don't know it.",
                        value: since,
                        onValueChange: setSince
                    }),
                    mode !== 'add' && /*#__PURE__*/ _jsx(SpecraDateField, {
                        label: mode === 'end' ? 'Ended on' : 'End date',
                        hint: mode === 'end' ? undefined : 'Only if it has ended.',
                        value: ended,
                        onValueChange: setEnded,
                        error: datesInvalid ? 'The end date is before the start date.' : undefined
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Notes",
                        htmlFor: "partner-notes",
                        hint: "e.g. what the partnership covers",
                        error: notesTooLong ? `At most ${NOTES_MAX} characters.` : undefined,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: "partner-notes",
                            rows: 3,
                            value: notes,
                            onChange: (e)=>setNotes(e.target.value)
                        })
                    }),
                    mode !== 'end' && /*#__PURE__*/ _jsx(DocumentPickerField, {
                        label: designation?.sourceArtifactId ? 'Source document (replace)' : 'Source document',
                        value: doc,
                        onChange: setDoc
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraOverlayFooter, {
                confirmLabel: mode === 'add' ? 'Mark as partner' : mode === 'end' ? 'End partnership' : 'Save',
                confirmDisabled: !canSave,
                loading: pending,
                onConfirm: save,
                onCancel: ()=>onOpenChange(false)
            })
        ]
    });
}
/**
 * Header action: "Mark as partner", or a Partner menu (Edit / End partnership /
 * Remove) while a partnership is open.
 */ export function PartnerControls({ companyId, companyName, designations, canEdit }) {
    const remove = useRemovePartner();
    const [mode, setMode] = useState(undefined);
    const [confirmRemove, setConfirmRemove] = useState(false);
    if (!canEdit) return null;
    const open = openDesignation(designations);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            open ? /*#__PURE__*/ _jsxs(DropdownMenu, {
                children: [
                    /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                        asChild: true,
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            variant: "outline",
                            "data-testid": Testids.registryCompany.partnerMenu,
                            children: [
                                "Partner",
                                /*#__PURE__*/ _jsx(ChevronDown, {
                                    className: "ml-1 size-4",
                                    "aria-hidden": "true"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                        align: "end",
                        children: [
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setMode('edit'),
                                children: "Edit partnership"
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setMode('end'),
                                children: "End partnership"
                            }),
                            /*#__PURE__*/ _jsx(DropdownMenuItem, {
                                onSelect: ()=>setConfirmRemove(true),
                                children: "Remove (recorded by mistake)"
                            })
                        ]
                    })
                ]
            }) : /*#__PURE__*/ _jsx(SpecraButton, {
                variant: "outline",
                onClick: ()=>setMode('add'),
                "data-testid": Testids.registryCompany.markPartner,
                children: "Mark as partner"
            }),
            /*#__PURE__*/ _jsx(PartnerDialog, {
                open: !!mode,
                onOpenChange: (o)=>!o && setMode(undefined),
                companyId: companyId,
                companyName: companyName,
                mode: mode ?? 'add',
                designation: open
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: confirmRemove && !!open,
                onOpenChange: (o)=>!o && setConfirmRemove(false),
                title: "Remove partnership",
                description: `Remove the partnership with ${companyName}? Use this only when it was recorded by mistake. It will not show as a former partnership; to keep the history, end the partnership instead.`,
                confirmLabel: "Remove",
                pendingLabel: "Removing…",
                isPending: remove.isPending,
                onConfirm: ()=>{
                    if (open) remove.mutate(open.id, {
                        onSettled: ()=>setConfirmRemove(false)
                    });
                }
            })
        ]
    });
}
